import { isCanceled } from "./eventDomain";
import { isHomeEligible } from "./occurrenceParticipation";

// Timeline items are heterogeneous; a post and an occurrence can share a
// numeric id, and one occurrence yields both a start and an end item.
export const itemKey = (item) => `${item.type}-${item.data.id}`;

const isEventItem = (item) => item.type === "event_start" || item.type === "event_end";

// A start card in the occurrence preview's shape: what a friend of the host
// gets who isn't taking part (invited or declined friends included). It has
// no participation fields, so attendance can't be judged from it — only that
// it isn't canceled. Rendering only; never a permission (that's the preview's
// can_view_full_detail, fetched when it's opened).
export const isFriendStartCard = (item) =>
  item.type === "event_start" && item.data != null && "can_view_full_detail" in item.data;

// Home lists what the viewer attends, plus friends' start cards. The backend
// never returns canceled, pending or declined occurrences of the viewer's own
// there; dropped here too so a stale page can never show one
// (isHomeEligible) — a date declined on its own included, though Calendar
// still shows it.
export const isShownOnHome = (item) => {
  if (!isEventItem(item)) return true;
  if (isFriendStartCard(item)) return !isCanceled(item.data);
  return isHomeEligible(item.data);
};

// /home's next_event, as shown (null unless the viewer attends it).
export const shownNextEvent = (occurrence) => (occurrence && isHomeEligible(occurrence) ? occurrence : null);

// Applies an announced occurrence change (eventChanges) to Home's rows and
// Next Event at once, before the re-read: a lost occurrence's rows go, as do a
// confirmed result's that's no longer attended; Next Event is cleared if it
// was that occurrence (the re-read fetches the new one). Only that
// occurrence's rows change. → { items, nextEvent, nextEventChanged }
export function applyChangeToHome({ items, nextEvent }, change) {
  const { kind, occurrenceId, occurrence } = change || {};
  let removedId = null;
  if (kind === "occurrenceAccessLost") removedId = occurrenceId;
  else if (kind === "occurrenceUpdated" && occurrence && !isHomeEligible(occurrence)) removedId = occurrence.id;
  if (removedId == null) return { items, nextEvent, nextEventChanged: false };
  const nextEventChanged = nextEvent?.id === removedId;
  return {
    items: items.filter((item) => !(isEventItem(item) && item.data.id === removedId)),
    nextEvent: nextEventChanged ? null : nextEvent,
    nextEventChanged,
  };
}

// Appends `page` to `items` in backend order (never re-sorted), skipping rows
// already present.
export function mergeHomePage(items, page) {
  const seen = new Set(items.map(itemKey));
  return [...items, ...(page || []).filter((item) => isShownOnHome(item) && !seen.has(itemKey(item)))];
}

// Re-reads the timeline from the top until at least `depth` rows are loaded
// (or it runs out, or `maxPages` pages were read), for a refresh that keeps
// the user's scroll depth. `fetchPage(cursor)` is GET /home/. `isCurrent()`
// lets a superseded refresh stop early (returns null).
// → { items, nextEvent, nextCursor, hasMore }
export async function fetchHomeToDepth(fetchPage, { depth, maxPages = 10, isCurrent = () => true }) {
  let items = [];
  let nextEvent = null;
  let cursor = null;
  let more = false;
  let pages = 0;
  do {
    const data = await fetchPage(cursor);
    if (!isCurrent()) return null;
    if (pages === 0) nextEvent = shownNextEvent(data.next_event);
    items = mergeHomePage(items, data.items);
    cursor = data.next_cursor || null;
    more = Boolean(data.has_more && cursor);
    pages += 1;
  } while (more && items.length < depth && pages < maxPages);
  return { items, nextEvent, nextCursor: more ? cursor : null, hasMore: more };
}
